import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useToasts — a tiny toast queue for MCPlayback action feedback + Undo (2c). The app had no generic
 * toast surface (legacy playback has only a download-specific stack), so this is the reusable one
 * (feature-inventory §227 "build once, reused").
 *
 * push() returns an id; a toast auto-dismisses after `duration` ms (pass duration:0 to pin it).
 * Undo is just a toast with an onUndo callback (the Toasts component renders the button). Timers are
 * cleared on dismiss and on unmount so a late timer can't setState a torn-down view. Ids come from a
 * module counter (not Date.now) so they are deterministic under test.
 */
let _toastId = 0;

export const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback(({ message, tone = 'info', undoLabel, onUndo, duration = 5000, dismissable = true } = {}) => {
    const id = ++_toastId;
    setToasts((list) => [...list, { id, message, tone, undoLabel, onUndo, dismissable }]);
    if (duration > 0) {
      timers.current[id] = setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  // Clear any pending timers on unmount.
  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
    timers.current = {};
  }, []);

  return { toasts, push, dismiss };
};
